import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Helmet } from "react-helmet";
import { ArrowRight, Layers, ArrowLeft } from "lucide-react";

export default function InchLandingPage() {
  const { inchSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/landing/inch/${inchSlug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [inchSlug]);

  if (loading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        Loading...
      </div>
    );
  if (!data)
    return (
      <div className="mx-auto max-w-[1200px] px-5 py-20 text-center">
        <h2 className="font-display text-3xl text-zinc-900">
          {inchSlug} inch not found
        </h2>
        <Link
          to="/conversion"
          className="mt-4 inline-block text-nibro-sage-dark hover:underline"
        >
          View size table
        </Link>
      </div>
    );

  return (
    <div className="min-h-screen bg-white" data-testid="inch-landing-page">
      <Helmet>
        <title>{data.seo.title}</title>
        <meta name="description" content={data.seo.description} />
        <meta property="og:title" content={data.seo.title} />
        <meta property="og:description" content={data.seo.description} />
        <link rel="canonical" href={`${window.location.origin}/inch/${data.inch_slug}`} />
      </Helmet>

      <div className="bg-nibro-sage-pale py-12">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <Link
            to="/conversion"
            className="mb-4 inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900"
          >
            <ArrowLeft size={14} /> All sizes
          </Link>
          <div className="nibro-frame">
            <div className="text-center">
              <h1 className="nibro-section-title" data-testid="inch-landing-h1">
                {data.seo.h1}
              </h1>
              <p className="mt-3 text-zinc-600">
                {data.product_count} pipe clamp products for {data.inch} pipes
                (= DN{data.dn}, outer diameter Ø {data.d1_mm} mm).
                Complete sets, halves and DIN 3567 heavy-duty in stainless 304/316.
              </p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-4">
            <SizeChip label="Inch" value={data.inch} />
            <SizeChip label="DN" value={`DN${data.dn}`} />
            <SizeChip label="D1 outer" value={`${data.d1_mm} mm`} />
            <SizeChip label="Products" value={data.product_count} />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 py-12 lg:px-10">
        {data.product_count === 0 ? (
          <div className="rounded-md border border-zinc-200 bg-white p-12 text-center">
            <Layers size={32} className="mx-auto text-zinc-400" />
            <div className="mt-3 font-display text-xl">
              No products for {data.inch}
            </div>
            <Link
              to="/products"
              className="mt-4 inline-flex items-center gap-2 text-sm text-nibro-sage-dark hover:underline"
            >
              Browse all products <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-6 flex items-center justify-between">
              <div className="label-uppercase">Compatible products</div>
              <Link
                to={`/dn/${data.dn}`}
                className="text-sm font-semibold text-nibro-sage-dark hover:underline"
                data-testid="inch-to-dn-link"
              >
                See DN{data.dn} →
              </Link>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {data.products.map((p) => (
                <ProductCard key={p.id} p={p} dnContext={data.dn} />
              ))}
            </div>
          </>
        )}

        <div className="mt-16 rounded-md border border-nibro-sage/30 bg-nibro-sage-pale p-8">
          <h2 className="font-display text-2xl font-bold text-zinc-900">
            About {data.inch} pipe clamps (DN{data.dn} / Ø{data.d1_mm}mm)
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-6 text-sm leading-relaxed text-zinc-700 md:grid-cols-2">
            <p>
              <strong>{data.inch}</strong> imperial pipe size matches an outer
              diameter of approximately <strong>{data.d1_mm} mm</strong>, equivalent to{" "}
              <strong>DN{data.dn}</strong> in metric. NIBRO manufactures{" "}
              {data.product_count} clamp variants for this exact pipe size in
              stainless 304 (V2A) and 316 (V4A) — complete sets with bolts and nuts,
              individual halves and DIN 3567 heavy-duty.
            </p>
            <p>
              All {data.inch} clamps are produced in our Oisterwijk facility using
              European stainless steel strip with our signature double-bent
              profile. Standard 24h delivery from stock — orders above €500 ship
              free within NL/BE/DE.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SizeChip({ label, value }) {
  return (
    <div className="rounded-md border-2 border-nibro-sage bg-white p-4 text-center">
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-nibro-sage-dark">
        {label}
      </div>
      <div className="mt-1 font-display text-2xl font-bold text-zinc-900">
        {value}
      </div>
    </div>
  );
}
